import { faArrowLeft, faCheck } from '@fortawesome/pro-regular-svg-icons';
import { Theme, useMediaQuery } from '@mui/system';
import { Elements } from '@stripe/react-stripe-js';
import { Stripe } from '@stripe/stripe-js';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from 'src/components/Avatar';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import {
  ConfirmDialog,
  useConfirmDialogComponent,
} from 'src/components/ConfirmDialog';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Link } from 'src/components/Link';
import { Loader } from 'src/components/Loader';
import { Collapse } from 'src/components/transitions/Collapse';
import { useSession } from 'src/contexts/SessionContext';
import { loadStripe } from 'src/models/cart/utils';
import { cancelTerminalAction } from 'src/models/terminal/terminal';
import { Trainer } from 'src/models/trainer/_types';
import { User } from 'src/models/user/_types';
import { getUserInitials } from 'src/models/user/utils';
import {
  SidebarDialog,
  SidebarDialogContent,
  SidebarDialogFab,
  SidebarDialogSidebar,
} from 'src/shared/SidebarDialog';
import { selectableItemTypes } from 'src/shared/constants/itemTypes';
import { Identifier } from 'src/types/utilityTypes';
import toast from 'src/utils/toast';

import { Bag } from './Bag';
import ItemSelector from './ItemSelector';
import PaymentComplete from './PaymentComplete';
import {
  PaymentDialogProvider,
  PaymentStep,
  usePaymentDialogContext,
} from './PaymentDialogContext';
import { PaymentDialogItemSettings } from './PaymentDialogItemSettings';
import PaymentMethodSelector from './PaymentMethodSelector';
import PaymentSummary from './PaymentSummary';
import { TypeSelector } from './TypeSelector';
import { UserSelector } from './UserSelector';
import { UserSelectorChip } from './UserSelectorChip';

interface PaymentDialogProps {
  open: boolean;
  onClose: () => void;
  user: User | undefined;
  itemType?: 'client' | 'group' | 'package' | 'product' | 'plan';
  onPaymentComplete?: () => void;
  trainer?: Trainer;
  initialFilterParams?: Record<string, any>;
  retryFromCartId?: Identifier | null;
  retryCartUserId?: Identifier;
}

interface Step {
  title: string;
  content: React.ReactNode | null;
  subtitle?: React.ReactNode;
  adornment?: React.ReactNode;
  actions?: React.ReactNode;
}

const PaymentDialogContent: React.FC<PaymentDialogProps> = ({
  open,
  onClose,
  onPaymentComplete,
  trainer,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'paymentDialog' });
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const {
    currentStep,
    prevStep: originalPrevStep,
    setCurrentStep,
    goToSelectUser,
    debouncedSearchValue,
    cart,
    reset,
    selectedItemType,
    resetSelectedItemType,
    itemToEdit,
    addToCart,
    removeFromCart,
    handleEditItem,
    handleSaveItemSettings,
    setSelectedPaymentMethod,
    setCheckNumber,
    setItemToEdit,
    updateLocationId,
    selectedUser,
    isRetryHydrating,
  } = usePaymentDialogContext();

  const [confirmProps, confirm] = useConfirmDialogComponent({
    title: t('itemsStillInBag'),
    confirmText: t('closeBag'),
    cancelText: t('continueShopping'),
    confirmColor: 'neutral',
  });

  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const { session } = useSession();

  useEffect(() => {
    if (session && !stripePromise) {
      setStripePromise(loadStripe(session));
    }
  }, [session, stripePromise]);

  const cancelTerminalPayment = async () => {
    // Check if we have an active terminal payment in progress
    if (
      typeof cart.paymentMethod === 'object' &&
      cart.paymentMethod?.type === 'card_present' &&
      cart.paymentMethod?.id
    ) {
      try {
        // Cancel the terminal action
        await cancelTerminalAction(cart.paymentMethod.id as string);
        // Don't need to wait for this - just fire and continue
      } catch (error) {
        // Non-critical error, can be ignored
        // eslint-disable-next-line no-console
        console.error('Error cancelling terminal action:', error);
      }
    }
  };

  const handleClose = async () => {
    // Don't show confirmation dialog after payment is complete
    if (
      cart?.cartItems &&
      cart.cartItems.length !== 0 &&
      currentStep !== PaymentStep.PaymentComplete
    ) {
      const confirmed = await confirm(t('closeCartWarning'));
      if (!confirmed) {
        return;
      }
    }

    if (
      currentStep === PaymentStep.Confirmation ||
      currentStep === PaymentStep.SelectPayment
    ) {
      await cancelTerminalPayment();
    }

    reset();
    onClose();
  };

  // Override prevStep to add terminal cancellation
  const prevStep = async () => {
    // If going back from confirmation to payment screen, and using terminal payment
    if (currentStep === PaymentStep.Confirmation) {
      await cancelTerminalPayment();
    }

    originalPrevStep();
  };

  const showSidebar =
    !mdDown &&
    (currentStep === PaymentStep.SelectType ||
      currentStep === PaymentStep.SelectItems ||
      currentStep === PaymentStep.Bag);

  const showFab =
    currentStep === PaymentStep.Bag || currentStep === PaymentStep.SelectItems;

  const backButton = (
    <IconButton onClick={prevStep} color="secondary">
      <Icon icon={faArrowLeft} />
    </IconButton>
  );

  const steps: { [key in PaymentStep]: Step } = {
    [PaymentStep.SelectUser]: {
      title: t('selectUser'),
      subtitle: t('selectUserSubtitle'),
      content: <UserSelector />,
    },
    [PaymentStep.SelectType]: {
      title: t('addToClient'),
      content: <TypeSelector trainer={trainer} />,
    },
    [PaymentStep.SelectItems]: {
      title:
        (selectedItemType &&
          selectableItemTypes.find((t) => t.value === selectedItemType)
            ?.label) ||
        (debouncedSearchValue ? t('searchingAllItems') : t('addToClient')),
      adornment: showSidebar ? undefined : backButton,
      content: (
        <Box>
          {!showSidebar && selectedUser && (
            <UserSelectorChip
              user={selectedUser}
              onChangeUser={goToSelectUser}
            />
          )}
          <ItemSelector
            onSelect={addToCart}
            onRemove={removeFromCart}
            cartItems={cart?.cartItems || []}
            trainer={trainer}
            cart={cart}
            updateLocationId={updateLocationId}
          />
        </Box>
      ),
    },
    [PaymentStep.EditItem]: {
      title: t('editItem'),
      adornment: backButton,
      content: itemToEdit && (
        <PaymentDialogItemSettings
          item={itemToEdit}
          onSave={handleSaveItemSettings}
          editing={true}
        />
      ),
    },
    [PaymentStep.Bag]: {
      title: t('bag'),
      subtitle: mdDown ? (
        <Link onClick={prevStep}>{t('continueShopping')}</Link>
      ) : undefined,
      content: (
        <Bag
          user={selectedUser}
          onRemove={removeFromCart}
          onEdit={handleEditItem}
        />
      ),
    },
    [PaymentStep.SelectPayment]: {
      title: t('paymentMethod'),
      adornment: backButton,
      actions: (
        <>
          <Button
            fullWidth
            variant="prominent"
            onClick={() => setCurrentStep(PaymentStep.Confirmation)}
            disabled={!cart.paymentMethod}
          >
            {t('reviewOrder')}
          </Button>
        </>
      ),
      content: (
        <PaymentMethodSelector
          cart={cart}
          user={selectedUser}
          onSelect={setSelectedPaymentMethod}
          onCheckNumberChange={setCheckNumber}
          goToSelectUser={goToSelectUser}
          value={
            typeof cart.paymentMethod === 'string'
              ? cart.paymentMethod
              : cart.paymentMethod?.id
          }
        />
      ),
    },
    [PaymentStep.Confirmation]: {
      title: t('orderSummary'),
      adornment: backButton,
      content: (
        <Elements stripe={stripePromise}>
          <PaymentSummary user={selectedUser} cart={cart} />
        </Elements>
      ),
    },
    [PaymentStep.PaymentComplete]: {
      title: t('paymentComplete'),
      content: (
        <PaymentComplete
          onPaymentDialogClose={handleClose}
          onPaymentComplete={onPaymentComplete}
        />
      ),
    },
  };

  const stepObj = steps[currentStep];

  useEffect(() => {
    // Reset selected type once selecting payment
    if (currentStep === PaymentStep.SelectPayment) {
      resetSelectedItemType();
    }
    // Dont allow SelectType on desktop (because its already in the sidebar)
    if (currentStep === PaymentStep.SelectType && !mdDown) {
      setCurrentStep(PaymentStep.SelectItems);
      resetSelectedItemType();
    }
    // Move to results if user starts typing in search
    if (currentStep === PaymentStep.SelectType && debouncedSearchValue) {
      setCurrentStep(PaymentStep.SelectItems);
    }
    if (
      currentStep === PaymentStep.SelectItems &&
      !selectedItemType &&
      !debouncedSearchValue &&
      mdDown
    ) {
      setCurrentStep(PaymentStep.SelectType);
      resetSelectedItemType();
    }
  }, [
    currentStep,
    resetSelectedItemType,
    selectedItemType,
    setCurrentStep,
    debouncedSearchValue,
    mdDown,
  ]);

  const validateCartItems = () => {
    if (session?.requireChargedBy) {
      // Check if any cart items don't have chargedById set
      const invalidItem = cart.cartItems.find((item) => !item.chargedById);
      if (invalidItem) {
        setItemToEdit(invalidItem);
        setCurrentStep(PaymentStep.EditItem);
        toast.error(t('chargedByRequired'));
        return false;
      }
    }
    return true;
  };

  const handleProceedToCheckout = () => {
    if (validateCartItems()) {
      setCurrentStep(PaymentStep.SelectPayment);
    }
  };

  const fabText =
    currentStep === PaymentStep.Bag
      ? t('proceedToCheckout')
      : t('itemsInBag', { count: cart.cartItems.length });

  // Mobile content switching: on mobile, show TypeSelector if on SelectType step, otherwise show current step content
  const sidebarContent = steps[PaymentStep.SelectType].content;
  const mainContent =
    isRetryHydrating ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <Loader />
      </Box>
    ) : mdDown && currentStep === PaymentStep.SelectType ? (
      sidebarContent
    ) : (
      stepObj.content
    );

  return (
    <>
      <SidebarDialog
        open={open}
        onClose={handleClose}
        title={stepObj.title}
        subtitle={stepObj.subtitle}
        leftAdornment={stepObj.adornment}
        maxWidth="md"
        actions={stepObj.actions}
      >
        <SidebarDialogSidebar open={showSidebar}>
          {sidebarContent}
        </SidebarDialogSidebar>
        <SidebarDialogContent>{mainContent}</SidebarDialogContent>
        {showFab && (
          <SidebarDialogFab
            text={fabText}
            startAdornment={
              <Collapse
                orientation="horizontal"
                in={
                  currentStep !== PaymentStep.Bag && cart.cartItems.length > 0
                }
              >
                <Icon icon={faCheck} sx={{ mr: 0.5, mt: 0.5 }} />
              </Collapse>
            }
            endAdornment={
              selectedUser && (
                <Avatar
                  src={selectedUser.profileImage}
                  initials={getUserInitials(selectedUser)}
                  sx={{ ml: 2 }}
                />
              )
            }
            active={cart.cartItems.length > 0}
            disabled={cart.cartItems.length === 0}
            onClick={() =>
              currentStep === PaymentStep.Bag
                ? handleProceedToCheckout()
                : setCurrentStep(PaymentStep.Bag)
            }
          />
        )}
      </SidebarDialog>
      <ConfirmDialog {...confirmProps} />
    </>
  );
};

export const PaymentDialog: React.FC<PaymentDialogProps> = (props) => (
  <PaymentDialogProvider
    user={props.user}
    itemType={props.itemType}
    initialFilterParams={props.initialFilterParams}
    open={props.open}
    retryFromCartId={props.retryFromCartId}
    retryCartUserId={props.retryCartUserId}
    onClose={props.onClose}
  >
    <PaymentDialogContent {...props} />
  </PaymentDialogProvider>
);
